import { Fragment } from "react";
import { Clock, Warning, Truck, Ghost } from "@phosphor-icons/react";

export const formatEncomendaNum = (value, decimals = 2) => {
  return new Intl.NumberFormat('pt-PT', { minimumFractionDigits: decimals, maximumFractionDigits: decimals }).format(value || 0);
};

const isCntPickup = (orderRef) => (orderRef || '').trim().toUpperCase().startsWith('CNT');

// Groups the flat item list of a client into one block per encomenda (order), preserving
// document order - a single order/Ref can cover several products, so consecutive items
// sharing the same order_number belong to the same header block.
export const groupEncomendaItemsByOrder = (items) => {
  const groups = [];
  let current = null;
  (items || []).forEach((item, globalIndex) => {
    const key = item.order_number || `sem-numero-${globalIndex}`;
    if (!current || current.key !== key) {
      current = { key, order_number: item.order_number, order_ref: item.order_ref, order_date: item.order_date, items: [] };
      groups.push(current);
    }
    current.items.push({ ...item, globalIndex });
  });
  return groups;
};

// Renders the "Nº <encomenda> (<ref>) ... <data>" header blocks + product table for one
// client's pending orders - shared between the Encomendas Pendentes list page and the
// client's own "Encomendas" tab, so both look and behave identically.
export const EncomendaOrdersTable = ({ items, idx = 0 }) => {
  return (
    <div className="overflow-x-auto" data-testid={`encomenda-client-table-${idx}`}>
      <table className="w-full text-xs">
        <thead>
          <tr className="text-stone-500 border-b border-stone-100">
            <th className="w-12 px-2 py-2"></th>
            <th className="text-left px-4 py-2 font-semibold">Artigo</th>
            <th className="text-left px-4 py-2 font-semibold">Descrição</th>
            <th className="text-left px-4 py-2 font-semibold">Lote</th>
            <th className="text-right px-4 py-2 font-semibold">Quantidade</th>
            <th className="text-right px-4 py-2 font-semibold">Cativo</th>
            <th className="text-right px-4 py-2 font-semibold">Peso (kg)</th>
            <th className="text-left px-4 py-2 font-semibold">O.Carga</th>
            <th className="text-right px-4 py-2 font-semibold">Dias após enc.</th>
          </tr>
        </thead>
        <tbody>
          {groupEncomendaItemsByOrder(items).map((group, gi) => {
            const groupDelayed = group.items.some(i => i.is_delayed);
            const groupShortage = group.items.some(i => i.is_stock_shortage);
            const groupZombie = group.items.some(i => i.is_zombie);
            const groupIsCnt = isCntPickup(group.order_ref);
            return (
              <Fragment key={group.key}>
                <tr className={groupIsCnt ? 'bg-blue-50' : 'bg-stone-50'} data-testid={`encomenda-order-group-${idx}-${gi}`}>
                  <td className="w-12 px-2 py-2">
                    <div className="flex items-center justify-center gap-1">
                      {groupIsCnt && (
                        <span title="Recolha / material para levantar no cliente">
                          <Truck size={13} weight="fill" className="text-blue-600" />
                        </span>
                      )}
                      {groupZombie && (
                        <span title="Encomenda Zombie: mais de 60 dias úteis em carteira - verificar se deve ser anulada">
                          <Ghost size={13} weight="fill" className="text-red-500" />
                        </span>
                      )}
                      {groupDelayed && (
                        <span title="Encomenda com mais de 6 dias">
                          <Clock size={13} weight="fill" className="text-amber-500" />
                        </span>
                      )}
                      {groupShortage && (
                        <span title="Stock insuficiente reservado">
                          <Warning size={13} weight="fill" className="text-amber-500" />
                        </span>
                      )}
                    </div>
                  </td>
                  <td colSpan={8} className="px-4 py-2">
                    <div className="flex items-center justify-between gap-3">
                      <span className={`font-semibold truncate ${groupIsCnt ? 'text-blue-700' : 'text-stone-700'}`} data-testid={`encomenda-order-group-number-${idx}-${gi}`}>
                        Nº {group.order_number || '—'}
                        {group.order_ref && (
                          <span className={groupIsCnt ? 'text-blue-500 font-normal' : 'text-stone-400 font-normal'} data-testid={`encomenda-order-group-ref-${idx}-${gi}`}> ({group.order_ref})</span>
                        )}
                      </span>
                      <span className={groupIsCnt ? 'text-blue-500 flex-shrink-0' : 'text-stone-400 flex-shrink-0'} data-testid={`encomenda-order-group-date-${idx}-${gi}`}>
                        {group.order_date ? new Date(group.order_date).toLocaleDateString('pt-PT') : '—'}
                      </span>
                    </div>
                    {groupIsCnt && (
                      <p className="mt-1.5 inline-flex items-center gap-1.5 text-[11px] font-semibold text-blue-700 bg-blue-100 rounded-md px-2 py-1" data-testid={`encomenda-order-group-cnt-alert-${idx}-${gi}`}>
                        <Truck size={12} weight="fill" />
                        MATERIAL PARA LEVANTAR NO CLIENTE (Reclamação)
                      </p>
                    )}
                  </td>
                </tr>
                {group.items.map((item) => (
                  <tr
                    key={item.globalIndex}
                    className={`border-b border-stone-50 ${groupIsCnt ? 'bg-blue-50/40' : ''}`}
                    data-testid={`encomenda-item-row-${idx}-${item.globalIndex}`}
                  >
                    <td className="w-12 px-2 py-2.5"></td>
                    <td className={`px-4 py-2.5 font-mono whitespace-nowrap ${groupIsCnt ? 'text-blue-700' : 'text-stone-700'}`}>{item.artigo}</td>
                    <td className={`px-4 py-2.5 whitespace-nowrap ${groupIsCnt ? 'text-blue-700' : 'text-stone-600'}`}>{item.descricao}</td>
                    <td className={`px-4 py-2.5 whitespace-nowrap ${groupIsCnt ? 'text-blue-600' : 'text-stone-500'}`}>{item.lote || '—'}</td>
                    <td className={`px-4 py-2.5 text-right whitespace-nowrap ${groupIsCnt ? 'text-blue-700' : 'text-stone-700'}`}>{formatEncomendaNum(item.encomenda_qty, 3)} {item.um}</td>
                    <td
                      className={`px-4 py-2.5 text-right whitespace-nowrap ${item.is_stock_shortage ? 'text-amber-600 font-semibold' : groupIsCnt ? 'text-blue-700' : 'text-stone-700'}`}
                      data-testid={`encomenda-item-shortage-${idx}-${item.globalIndex}`}
                    >
                      {item.afectado_date ? new Date(item.afectado_date).toLocaleDateString('pt-PT') : formatEncomendaNum(item.afectado_qty, 3)}
                    </td>
                    <td className={`px-4 py-2.5 text-right whitespace-nowrap ${groupIsCnt ? 'text-blue-700' : 'text-stone-700'}`}>{formatEncomendaNum(item.peso, 0)}</td>
                    <td className={`px-4 py-2.5 whitespace-nowrap ${groupIsCnt ? 'text-blue-600' : 'text-stone-500'}`}>{item.o_carga || '—'}</td>
                    <td className={`px-4 py-2.5 text-right whitespace-nowrap ${item.is_zombie ? 'text-red-600 font-semibold' : item.is_delayed ? 'text-amber-600 font-semibold' : groupIsCnt ? 'text-blue-700' : 'text-stone-700'}`} data-testid={`encomenda-item-days-${idx}-${item.globalIndex}`}>
                      {item.days_since_order != null ? `${item.days_since_order}d` : '—'}
                    </td>
                  </tr>
                ))}
              </Fragment>
            );
          })}
        </tbody>
      </table>
    </div>
  );
};

